import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  FilterPanel,
  Icon,
  IconButton,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@playerelite/ui";
import type { FilterGroup, TabsDotColor } from "@playerelite/ui";
import { UserStore } from "../..";
import {
  createKioskAttractor,
  deleteKioskAttractor,
  getKioskAttractors,
  getSingleUploadPresignedS3Url,
  updateKioskAttractor,
  updateKioskAttractorTimer,
  uploadToS3PresignedUrl,
} from "../../api/new-api";
import { toClassificationRequest } from "../../components/attractor-content";
import { KioskAttractor } from "../../types";
import { runAuthHandler } from "../util";
import AttractorCampaignCard, { attractorTitle } from "./AttractorCampaignCard";
import AttractorFormDialog, { AttractorDraft } from "./AttractorFormDialog";
import DeleteAttractorDialog from "./DeleteAttractorDialog";
import KioskSettingsDialog, { KioskSettings } from "./KioskSettingsDialog";
import {
  attractorsForTab,
  CampaignTab,
  CONTENT_FILTER_ID,
  endEpochFromIso,
  GAMING_ONLY_FILTER,
  matchesContentFilter,
  startEpochFromIso,
  visibilityFromDays,
} from "./campaign-utils";

const TABS: { value: CampaignTab; label: string; dot?: TabsDotColor }[] = [
  { value: "active", label: "Active", dot: "success" },
  { value: "future", label: "Future", dot: "info" },
  { value: "expired", label: "Expired", dot: "danger" },
  { value: "all", label: "All" },
];

const EMPTY_MESSAGES: Record<CampaignTab, string> = {
  active: "No attractors are showing right now.",
  future: "No attractors are scheduled to start.",
  expired: "No attractors have ended yet.",
  all: "This venue has no kiosk attractors yet.",
};

const FILTER_GROUPS: FilterGroup[] = [
  {
    id: CONTENT_FILTER_ID,
    label: "Filter by content type",
    options: [
      { value: "Game", label: "Game attractors" },
      { value: "Promotion", label: "Promotion attractors" },
      { value: "General", label: "General attractors" },
      { value: GAMING_ONLY_FILTER, label: "Gaming only attractors" },
    ],
  },
];

const clampPercent = (percent: number) =>
  Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;

/**
 * Kiosk Attractor Campaigns — the kiosk attractor screen rebuilt from @playerelite/ui, following
 * the kit's Pages/Kiosk Attractor Campaigns story. Served at /kiosk-attractors-1 alongside the
 * original page while it is reviewed on staging.
 */
const KioskAttractorCampaignsPage = observer(() => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;
  const venueId = selectedVenue?.venueId;
  const videoAllowed =
    selectedVenue?.venueFeatures?.videoAttractorsEnabled ?? false;
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<CampaignTab>("active");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KioskAttractor | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleting, setDeleting] = useState<KioskAttractor | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    runAuthHandler({ userStore, navigate });
    setNotice(null);
  }, [selectedVenue, userStore, navigate]);

  const queryKey = ["kiosk-attractors", venueId];
  const attractorsQuery = useQuery({
    queryKey,
    queryFn: async () => (await getKioskAttractors())?.data ?? null,
    enabled: venueId !== undefined,
    retry: 1,
  });
  const data = attractorsQuery.data;
  // Statuses are judged against when the list was fetched, which refreshes on window focus.
  const now = attractorsQuery.dataUpdatedAt || Date.now();

  const settings = useMemo<KioskSettings>(
    () => ({
      timeToChangeAttractor: data?.timeToChangeAttractor ?? 0,
      timeUntilBackgroundMode: data?.timeUntilBackgroundMode ?? 0,
      timeToRefreshBackendData: data?.timeToRefreshBackendData ?? 0,
    }),
    [data]
  );

  const filtered = (data?.attractors ?? []).filter((a) =>
    matchesContentFilter(a, filters[CONTENT_FILTER_ID])
  );
  const hasFilters = (filters[CONTENT_FILTER_ID] ?? []).length > 0;
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (attractor: KioskAttractor) => {
    setEditing(attractor);
    setFormOpen(true);
  };

  const submitAttractor = async (
    draft: AttractorDraft,
    reportProgress: (percent: number) => void
  ) => {
    const fields = {
      startDateEpoch: startEpochFromIso(
        draft.range.start,
        editing?.startDateEpoch
      ),
      endDateEpoch: endEpochFromIso(draft.range.end, editing?.endDateEpoch),
      dayVisibility: visibilityFromDays(draft.days),
      ...toClassificationRequest(draft.content),
    };

    if (editing) {
      const res = await updateKioskAttractor({
        attractorId: editing.attractorId,
        ...fields,
      }).catch(() => undefined);
      if (!res)
        throw new Error("The attractor couldn't be updated. Try again.");
      setNotice(`${attractorTitle(editing)} updated.`);
    } else {
      const media = draft.media as File;
      let mediaS3Key: string;
      try {
        reportProgress(0);
        const presigned = await getSingleUploadPresignedS3Url({
          imageFileName: media.name,
        });
        mediaS3Key = presigned.data.imageKey;
        await uploadToS3PresignedUrl({
          uploadUrl: presigned.data.imageUploadUrl,
          data: media,
          progressCallback: (percent) => reportProgress(clampPercent(percent)),
        });
      } catch {
        throw new Error(
          "The media couldn't be uploaded. Check your connection and try again."
        );
      }
      // createKioskAttractor swallows request errors and resolves with nothing.
      const res = await createKioskAttractor({ mediaS3Key, attractor: fields });
      if (!res || res.data?.success === false) {
        throw new Error("The attractor couldn't be created. Try again.");
      }
      setNotice("Attractor added.");
    }
    await refresh();
  };

  const submitSettings = async (next: KioskSettings) => {
    const res = await updateKioskAttractorTimer({
      KioskTimeToChangeAttractorSecs: next.timeToChangeAttractor,
      KioskTimeUntilBackgroundModeSecs: next.timeUntilBackgroundMode,
      KioskTimeToRefreshBackendDataSecs: next.timeToRefreshBackendData,
    }).catch(() => undefined);
    if (!res?.data?.success) {
      throw new Error("The kiosk settings couldn't be saved. Try again.");
    }
    setNotice("Kiosk settings saved.");
    await refresh();
  };

  const confirmDelete = async (attractor: KioskAttractor) => {
    const res = await deleteKioskAttractor({
      attractorId: attractor.attractorId,
    }).catch(() => undefined);
    if (!res) throw new Error("The attractor couldn't be deleted. Try again.");
    setNotice(`${attractorTitle(attractor)} deleted.`);
    await refresh();
  };

  const renderPanel = (forTab: CampaignTab) => {
    if (attractorsQuery.isPending) {
      return (
        <div className="flex justify-center py-12 text-fg-muted">
          <Spinner size="lg" label="Loading attractors" />
        </div>
      );
    }
    const list = attractorsForTab(filtered, forTab, now);
    if (list.length === 0) {
      return (
        <p className="py-12 text-center text-sm text-fg-muted">
          {hasFilters
            ? "No attractors match the current filters."
            : EMPTY_MESSAGES[forTab]}
        </p>
      );
    }
    return (
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {list.map((attractor) => (
          <AttractorCampaignCard
            key={attractor.attractorId}
            attractor={attractor}
            now={now}
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="bg-surface text-fg">
      <div className="mx-auto flex max-w-screen-xl flex-col gap-4 px-4 py-8 lg:px-6">
        <header>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-semibold focus:outline-none"
          >
            Kiosk Attractor Campaigns
          </h1>
          <p className="text-sm text-fg-muted">
            Manage and schedule the attractors displayed on your venue's kiosks.
          </p>
        </header>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="adminWhite"
            startIcon={<Icon name="gear" />}
            disabled={!data}
            onClick={() => setSettingsOpen(true)}
          >
            Kiosk Settings
          </Button>
          <Button
            variant="action"
            startIcon={<Icon name="plus" />}
            disabled={venueId === undefined}
            onClick={openAdd}
          >
            Add Attractor
          </Button>
        </div>

        {notice ? (
          <Alert
            variant="success"
            action={
              <IconButton
                aria-label="Dismiss"
                variant="ghost"
                size="sm"
                onClick={() => setNotice(null)}
              >
                <Icon name="x" size="sm" />
              </IconButton>
            }
          >
            {notice}
          </Alert>
        ) : null}

        {attractorsQuery.isError ? (
          <Alert
            variant="danger"
            title="Couldn't load attractors"
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => attractorsQuery.refetch()}
              >
                Try again
              </Button>
            }
          >
            Check your connection and try again.
          </Alert>
        ) : (
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as CampaignTab)}
            variant="admin"
            className="gap-6"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Scrolls on its own at phone width rather than widening the page. The padding
                  keeps focus rings from being clipped by the overflow. */}
              <div className="-m-1 max-w-full overflow-x-auto p-1">
                <TabsList aria-label="Attractor status">
                  {TABS.map((t) => (
                    <TabsTrigger
                      key={t.value}
                      value={t.value}
                      dot={t.dot}
                      count={
                        attractorsQuery.isPending
                          ? undefined
                          : attractorsForTab(filtered, t.value, now).length
                      }
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <FilterPanel
                groups={FILTER_GROUPS}
                value={filters}
                onValueChange={setFilters}
                align="end"
              />
            </div>

            {/* Every tab renders a panel so each trigger's aria-controls resolves. */}
            {TABS.map((t) => (
              <TabsContent key={t.value} value={t.value}>
                {renderPanel(t.value)}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      <AttractorFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        attractor={editing}
        venueId={venueId}
        videoAllowed={videoAllowed}
        onSubmit={submitAttractor}
      />

      <KioskSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSubmit={submitSettings}
      />

      <DeleteAttractorDialog
        attractor={deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        focusAfterDelete={headingRef}
      />
    </section>
  );
});

export default KioskAttractorCampaignsPage;
